<template>
  <main class="dashboard">
    <header class="dashboard__header">
      <div class="dashboard__brand">
        <img :src="brandLogo" alt="" class="dashboard__brand-logo" aria-hidden="true" />
        <span class="dashboard__brand-name">Wellnotes</span>
      </div>
      <div class="dashboard__header-actions">
        <span v-if="authState.userData && authState.userData.premium" class="dashboard__chip">
          <SfIcon name="gem" :size="13" :stroke-width="2.2" />
          {{ $t("dashboard.premium") }}
        </span>
        <router-link to="/profile" class="dashboard__profile" :aria-label="$t('profile.title')" :title="$t('profile.title')">
          <SfIcon name="profile" :size="30" :stroke-width="1.6" />
        </router-link>
      </div>
    </header>

    <div class="dashboard__layout">
      <FolderSidebar
        :notes="notesState.notes"
        :shared-notes="notesState.sharedNotes"
        :folders="notesState.folders"
        :active-category="uiState.activeCategory"
        :active-folder="uiState.activeFolder"
        @select-category="onSelectCategory"
        @select-folder="onSelectFolder"
      />

      <section class="dashboard__main">
        <h1 class="dashboard__title">{{ pageTitle }}</h1>

        <label class="dashboard__search">
          <SfIcon name="search" :size="17" class="dashboard__search-icon" />
          <input
            type="search"
            :placeholder="$t('notes.searchPlaceholder')"
            v-model="uiState.searchTerm"
            :aria-label="$t('notes.searchPlaceholder')"
          />
        </label>

        <div v-if="notesState.isLoading" class="dashboard__group" :aria-label="$t('notes.loading')" aria-busy="true">
          <div v-for="i in 6" :key="`note-skel-${i}`" class="note-row-skeleton" aria-hidden="true">
            <span class="skeleton-dot"></span>
            <span class="skeleton-lines">
              <span class="skeleton-line skeleton-line--title"></span>
              <span class="skeleton-line skeleton-line--text"></span>
            </span>
          </div>
        </div>

        <div v-else-if="notesState.error" class="dashboard__empty dashboard__empty--error">
          <p>{{ notesState.error }}</p>
        </div>

        <div v-else-if="filteredNotes.length === 0" class="dashboard__empty">
          <span class="dashboard__empty-art" aria-hidden="true">
            <img :src="emptyEmoji" alt="" width="72" height="72" decoding="async" />
          </span>
          <p v-if="uiState.searchTerm">{{ $t("notes.emptySearch") }}</p>
          <p v-else-if="uiState.activeFolder">{{ $t("notes.emptyFolder") }}</p>
          <p v-else-if="uiState.activeCategory === 'TRASH'">{{ $t("notes.emptyTrash") }}</p>
          <p v-else>{{ $t("notes.empty") }}</p>
        </div>

        <template v-else>
          <section v-for="group in groupedNotes" :key="group.key" class="dashboard__section">
            <h2 class="dashboard__section-title">{{ group.label }}</h2>
            <div class="dashboard__grid">
              <NoteCard v-for="note in group.notes" :key="note.id" :note="note" />
            </div>
          </section>
        </template>

        <div
          v-if="!notesState.isLoading && notesState.hasMore"
          ref="loadSentinel"
          class="dashboard__load-more"
        >
          <button
            type="button"
            class="dashboard__load-button"
            :disabled="notesState.isLoadingMore"
            @click="onLoadMore"
          >
            <SfIcon v-if="notesState.isLoadingMore" name="spinner" :size="14" class="dashboard__spin" />
            <span>{{ notesState.isLoadingMore ? $t("notes.loadingMore") : $t("notes.loadMore") }}</span>
          </button>
        </div>
      </section>
    </div>

    <NewNoteFab />
  </main>
</template>

<script>
import { authState } from "../stores/authStore";
import brandLogo from "../assets/icon.png";
import {
  notesState,
  filterNotes,
  loadMoreNotes,
  dashboardUiState,
} from "../stores/notesStore";
import { notePreview, noteTitle } from "../utils/markdown";
import { dateSection } from "../utils/dateFormat";
import FolderSidebar from "../components/FolderSidebar.vue";
import NoteCard from "../components/NoteCard.vue";
import NewNoteFab from "../components/NewNoteFab.vue";
import SfIcon from "../components/SfIcon.vue";
import { EMOJI } from "../assets";

export default {
  name: "DashboardPage",
  components: { FolderSidebar, NoteCard, NewNoteFab, SfIcon },
  data() {
    return {
      authState,
      notesState,
      // Selected category/folder/search live in the shared store so they
      // survive navigating to a note and back via "Back to notes".
      uiState: dashboardUiState,
      sentinelObserver: null,
      brandLogo,
    };
  },
  mounted() {
    this.$nextTick(() => this.attachSentinelObserver());
  },
  updated() {
    this.attachSentinelObserver();
  },
  beforeUnmount() {
    this.detachSentinelObserver();
  },
  computed: {
    pageTitle() {
      if (this.uiState.activeFolder) return this.uiState.activeFolder;
      return this.$t("notes.category." + (this.uiState.activeCategory || "ALL"));
    },
    // The same emoji iOS puts in its empty states: the detective for a
    // search with no match, the wastebasket for an empty trash, the writing
    // hand everywhere else.
    emptyEmoji() {
      if (this.uiState.searchTerm) return EMOJI.detective;
      if (this.uiState.activeCategory === "TRASH" && !this.uiState.activeFolder) return EMOJI.wastebasket;
      return EMOJI.writingHand;
    },
    groupedNotes() {
      const locale = (this.$i18n && this.$i18n.locale) || "en";
      const last7 = this.$t("notes.groupLast7Days");
      const groups = [];
      for (const note of this.filteredNotes) {
        const section = dateSection(note.timestamp, locale, last7);
        const current = groups[groups.length - 1];
        if (current && current.key === section.key) current.notes.push(note);
        else groups.push({ ...section, notes: [note] });
      }
      return groups;
    },
    filteredNotes() {
      let list = filterNotes(
        this.notesState.notes,
        this.uiState.activeCategory,
        this.uiState.activeFolder,
        this.notesState.sharedNotes,
      );
      const q = this.uiState.searchTerm.trim().toLowerCase();
      if (q.length > 0) {
        list = list.filter((n) => {
          const title = noteTitle(n.content || "").toLowerCase();
          if (title.includes(q)) return true;
          const preview = notePreview(n.content || "", 5).toLowerCase();
          return preview.includes(q);
        });
      }
      return list;
    },
  },
  methods: {
    onSelectCategory(key) {
      this.uiState.activeCategory = key;
      this.uiState.activeFolder = null;
    },
    onSelectFolder(name) {
      this.uiState.activeFolder = name;
    },
    onLoadMore() {
      loadMoreNotes();
    },
    attachSentinelObserver() {
      if (typeof IntersectionObserver === "undefined") return;
      const el = this.$refs.loadSentinel;
      if (!el) {
        this.detachSentinelObserver();
        return;
      }
      if (this.sentinelObserver && this.sentinelObserver._target === el) return;
      this.detachSentinelObserver();
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (
              entry.isIntersecting &&
              this.notesState.hasMore &&
              !this.notesState.isLoadingMore &&
              !this.notesState.isLoading
            ) {
              loadMoreNotes();
            }
          }
        },
        { rootMargin: "240px 0px" },
      );
      observer.observe(el);
      observer._target = el;
      this.sentinelObserver = observer;
    },
    detachSentinelObserver() {
      if (this.sentinelObserver) {
        try {
          this.sentinelObserver.disconnect();
        } catch (_) {
          // ignore
        }
        this.sentinelObserver = null;
      }
    },
  },
};
</script>

<style scoped>
.dashboard__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}

/* Mirrors the iOS main list: grouped grey page, large title, a filled
   search field, date sections of white inset-grouped rows. */
.dashboard {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto 120px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: var(--wn-label);
}

.dashboard__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 2px 0;
}

.dashboard__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.dashboard__brand-logo {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  object-fit: contain;
}

.dashboard__brand-name {
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.dashboard__header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dashboard__chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--wn-accent-soft);
  color: var(--wn-accent-text);
  font-size: 12.5px;
  font-weight: 700;
}

.dashboard__profile {
  display: inline-flex;
  color: var(--wn-accent);
  border-radius: 999px;
  transition: transform 0.12s ease;
}

.dashboard__profile:hover {
  transform: scale(1.06);
}

.dashboard__profile:focus-visible,
.dashboard__load-button:focus-visible {
  outline: 2px solid var(--wn-accent);
  outline-offset: 3px;
}

.dashboard__layout {
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.dashboard__main {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.dashboard__title {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.1;
  text-wrap: balance;
}

.dashboard__search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  background: var(--wn-fill);
  color: var(--wn-secondary);
  cursor: text;
}

.dashboard__search:focus-within {
  box-shadow: 0 0 0 2px var(--wn-accent);
}

.dashboard__search input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--wn-label);
  font: inherit;
  font-size: 15px;
}

.dashboard__search input::placeholder {
  color: var(--wn-secondary);
}

.dashboard__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}

.dashboard__section-title {
  margin: 0 4px;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.dashboard__group {
  display: flex;
  flex-direction: column;
  background: var(--wn-card);
  border-radius: var(--wn-radius);
  overflow: hidden;
}

.dashboard__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 24px;
  border-radius: var(--wn-radius);
  background: var(--wn-card);
  text-align: center;
  color: var(--wn-secondary);
  font-size: 15px;
}

.dashboard__empty p {
  margin: 0;
  max-width: 40ch;
  color: var(--wn-label);
  font-weight: 600;
}

.dashboard__empty-art {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255, 204, 0, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dashboard__empty-art img {
  width: 72px;
  height: 72px;
  object-fit: contain;
}

.dashboard__empty--error {
  color: #ff3b30;
}

.note-row-skeleton {
  display: flex;
  gap: 14px;
  padding: 15px 16px;
}

.note-row-skeleton + .note-row-skeleton {
  border-top: 0.5px solid var(--wn-separator);
}

.skeleton-dot {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: var(--wn-fill);
}

.skeleton-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-line {
  display: block;
  border-radius: 6px;
  background: var(--wn-fill);
  animation: dashboard-pulse 1.4s ease-in-out infinite;
}

.skeleton-line--title {
  height: 14px;
  width: 55%;
}

.skeleton-line--text {
  height: 11px;
  width: 85%;
}

@keyframes dashboard-pulse {
  50% {
    opacity: 0.45;
  }
}

.dashboard__load-more {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.dashboard__load-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: var(--wn-card);
  color: var(--wn-accent-text);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.dashboard__load-button:disabled {
  cursor: default;
  opacity: 0.7;
}

.dashboard__spin {
  animation: dashboard-spin 0.9s linear infinite;
}

@keyframes dashboard-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-line,
  .dashboard__spin {
    animation: none;
  }
}

@media (max-width: 860px) {
  .dashboard__layout {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .dashboard__title {
    font-size: 28px;
  }
}
</style>
